import { useMemo, useState } from 'react';
import { Button } from '@werkstatt/ui';
import { ImageOff } from 'lucide-react';

interface Props {
  html?: string | null;
  text?: string | null;
  /** Eingehende Mails enthalten fremdes HTML und werden strenger behandelt. */
  untrusted?: boolean;
}

/**
 * Rendert einen Mail-Inhalt in einem abgeschotteten `<iframe srcdoc>`.
 *
 * Kein `allow-scripts`, kein `allow-forms`, kein `allow-popups` — ohne diese
 * Rechte kann fremdes HTML weder Skripte ausführen noch das Backoffice
 * navigieren. Eine zusätzliche CSP im `srcdoc` blockt externe Ressourcen;
 * Bilder werden erst nach einem bewussten Klick nachgeladen (Tracking-Schutz,
 * docs/06-email-system.md §5.4).
 */
export function MessageBody({ html, text, untrusted = false }: Props) {
  const [showImages, setShowImages] = useState(!untrusted);

  const srcDoc = useMemo(() => {
    if (!html) return null;
    const imageSource = showImages ? "img-src data: https:;" : "img-src 'none';";
    const csp = `default-src 'none'; style-src 'unsafe-inline'; ${imageSource}`;
    return `<!DOCTYPE html><html><head>
<meta charset="utf-8">
<meta http-equiv="Content-Security-Policy" content="${csp}">
<style>
  html,body { margin:0; padding:0; }
  body { font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
         font-size: 14px; color: #1f2933; background: #ffffff; }
  img { max-width: 100%; height: auto; }
  a { pointer-events: none; color: #0a5adb; }
</style></head><body>${html}</body></html>`;
  }, [html, showImages]);

  if (!srcDoc) {
    return (
      <pre className="whitespace-pre-wrap break-words text-sm text-foreground/90">
        {text || 'Kein Inhalt'}
      </pre>
    );
  }

  return (
    <div className="space-y-2">
      {untrusted && !showImages && (
        <div className="flex items-center gap-2 rounded-md border border-border bg-muted/40 px-3 py-2 text-xs">
          <ImageOff className="h-3.5 w-3.5 shrink-0" aria-hidden />
          <span className="flex-1">
            Externe Bilder wurden blockiert. Sie können zur Nachverfolgung des
            Lesezeitpunkts genutzt werden.
          </span>
          <Button size="sm" variant="outline" onClick={() => setShowImages(true)}>
            Bilder laden
          </Button>
        </div>
      )}
      <iframe
        title="Nachrichtinhalt"
        sandbox=""
        srcDoc={srcDoc}
        className="w-full rounded-md border border-border bg-white"
        style={{ height: 420 }}
      />
    </div>
  );
}
